import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { usePostCreateTemplateMutation, usePostEditTemplateMutation } from 'apis/report-templates.api';
import {
  getAggregateFunctionNumber,
  getDisplayTypeFromDataType,
  typeStringMap,
  VisualizationType,
  visualizationTypeMap,
} from 'constants/reports-templates';
import { useOpen } from 'hooks/useOpen';
import { CommonError } from 'interfaces/shared.interface';
import { ErrorModal, Input, Modal } from 'shared-components';
import ModalControlButtons from 'shared-components/ModalControlButtons';
import { successNotify } from 'utils/notifications';

interface CreateTemplateModalProps {
  onCloseModal: () => void;
  getConfiguration: () => any | null;
  isOpenModal: boolean;
  selectedColumnsData: any[];
  initialName?: string;
  filtersBlockRef: React.RefObject<any>;
  isEditMode?: boolean;
  templateId?: string;
}

const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  onCloseModal,
  getConfiguration,
  isOpenModal,
  selectedColumnsData,
  initialName,
  isEditMode,
  templateId,
}) => {
  const [postCreateTemplate, { error: createTemplateError }] = usePostCreateTemplateMutation();
  const [isOpenErrorModal, handleOpenErrorModal, handleOnCloseErrorModal] = useOpen();
  const [name, setName] = useState(initialName || '');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const errorData = createTemplateError as CommonError;
  const errorDetails = errorData?.data?.error as { message?: string; details?: string };
  const [updateTemplate] = usePostEditTemplateMutation();

  const handleCreate = async () => {
    const config = getConfiguration();

    if (!config || typeof config !== 'object' || !config.tableName) {
      return;
    }

    const { tableName, joins = [], groupBy, aggregates = [], filters = [], visualization } = config;

    const extractRawColumnName = (fullName: string): string => {
      const parts = fullName.trim().split(' ');
      return parts[parts.length - 1];
    };

    const rawColumnName =
      visualization.type === 'Table'
        ? visualization.columns?.[0] ?? ''
        : extractRawColumnName(visualization.column ?? '');

    const columnMeta = selectedColumnsData.find((col) => col.name === rawColumnName && col.tableName === tableName);

    const isCountVisualization = visualization.column === 'Count';
    if (!columnMeta && visualization.type !== 'Table' && !isCountVisualization) {
      return;
    }

    const getColumnInfo = (name: string, tableName: string) => {
      const meta = selectedColumnsData.find((col) => col.name === name && col.tableName === tableName);
      if (!meta) {
        return null;
      }

      return {
        name: meta.name,
        tableName: meta.tableName,
        type: meta.columnType,
        displayType: getDisplayTypeFromDataType(typeStringMap[meta.columnType]),
      };
    };

    const normalizedJoins = joins.map((join: any) => {
      const left = getColumnInfo(join.leftColumn, join.leftTable);
      const right = getColumnInfo(join.rightColumn, join.rightTable);
      return {
        column: left,
        joinColumn: right,
        tableName: join.leftTable,
      };
    });

    const groupByColumn =
      Array.isArray(groupBy) && groupBy.length > 0 ? getColumnInfo(groupBy[0].name, groupBy[0].tableName) : undefined;

    const matchingAggregate = aggregates.find((agg: any) => {
      const funcMatches = agg.func?.toLowerCase() === visualization.column?.split(' ')[0]?.toLowerCase();

      if (agg.func === 'Count') {
        return funcMatches;
      }

      const colMatches = agg.column ? agg.column.toLowerCase() === rawColumnName.toLowerCase() : true;
      return funcMatches && colMatches;
    });

    const displayColumns =
      config.query?.displayColumns ||
      (visualization.type === 'Table'
        ? visualization.columns
            .map((colName: string) => {
              const meta = selectedColumnsData.find((col) => col.name === colName && col.tableName === tableName);
              if (!meta) {
                return null;
              }

              return {
                ...getColumnInfo(meta.name, meta.tableName),
                displayName: meta.name,
              };
            })
            .filter(Boolean)
        : [
            isCountVisualization
              ? {
                  name: 'Count',
                  tableName: tableName,
                  type: 3,
                  displayType: 'BIGINT',
                  displayName: visualization.column,
                  aggregateFunctionType: matchingAggregate
                    ? getAggregateFunctionNumber(matchingAggregate.func)
                    : undefined,
                }
              : columnMeta
                ? {
                    ...getColumnInfo(columnMeta.name, columnMeta.tableName),
                    displayName: visualization.column,
                    aggregateFunctionType: matchingAggregate
                      ? getAggregateFunctionNumber(matchingAggregate.func)
                      : undefined,
                  }
                : {
                    name: isCountVisualization ? 'Count' : rawColumnName,
                    tableName: tableName,
                    type: 3,
                    displayType: 'BIGINT',
                    displayName: visualization.column,
                    aggregateFunctionType: matchingAggregate
                      ? getAggregateFunctionNumber(matchingAggregate.func)
                      : undefined,
                  },
          ]);

    const normalizedFilters = filters.map((filter: any) => {
      const meta = selectedColumnsData.find((col) => col.name === filter.column && col.tableName === tableName);
      return {
        column: {
          name: meta?.name,
          tableName: meta?.tableName,
          displayName: meta?.name,
          displayType: getDisplayTypeFromDataType(typeStringMap[meta?.columnType]),
          type: meta?.columnType,
        },
        condition: filter.condition,
        isLocked: filter.locked,
        value: filter.value,
      };
    });

    const parseDirection = (value: string | number | undefined): number => {
      if (typeof value === 'string') {
        switch (value.toLowerCase()) {
          case 'asc':
            return 1;
          case 'desc':
            return 2;
          default:
            return 1;
        }
      }
      return typeof value === 'number' ? value : 1;
    };

    const normalizeSorting = (sortingConfig: any[], getColumnInfo: any, defaultTable: string) => {
      return sortingConfig
        .map((sorting: any, index: number) => {
          const columnName = typeof sorting.column === 'string' ? sorting.column : sorting.column?.name;
          const tableName = typeof sorting.column === 'string' ? defaultTable : sorting.column?.tableName;

          const columnInfo = getColumnInfo(columnName, tableName);
          if (!columnInfo) {
            return null;
          }

          return {
            column: columnInfo,
            direction: parseDirection(sorting.direction),
            order: index + 1,
          };
        })
        .filter(Boolean);
    };

    const sorting =
      Array.isArray(config.sorting) && config.sorting.length > 0
        ? normalizeSorting(config.sorting, getColumnInfo, tableName)
        : undefined;

    const basePayload = {
      name: name.trim(),
      distributionColumn:
        visualization.type === 'Table'
          ? {
              name: visualization.columns?.[0],
              tableName: tableName,
              columnType: selectedColumnsData.find((col) => col.name === visualization.columns?.[0])?.columnType ?? 0,
            }
          : columnMeta
            ? {
                name: columnMeta.name,
                tableName: columnMeta.tableName,
                columnType: columnMeta.columnType,
              }
            : {
                name: isCountVisualization ? 'Count' : rawColumnName,
                tableName: tableName,
                columnType: 0,
              },
      query: {
        tableName,
        joins: normalizedJoins,
        filters: normalizedFilters,
        ...(groupByColumn ? { groupBy: groupByColumn } : {}),
        ...(sorting ? { sorting } : {}),
        displayColumns,
      },
      visualizationTypeId: visualizationTypeMap[visualization.type as VisualizationType],
    };

    setLoading(true);

    try {
      if (isEditMode && templateId) {
        const editPayload = {
          id: templateId,
          ...basePayload,
        };
        await updateTemplate(editPayload).unwrap();
        successNotify(t('SuccessfullyUpdated'));
      } else {
        await postCreateTemplate(basePayload).unwrap();
        successNotify(t('SuccessfullyCreated'));
      }

      navigate('/management/report-templates');
    } catch (error) {
      handleOpenErrorModal();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ErrorModal
        errorMessage={errorDetails?.message}
        description={errorDetails?.details}
        isOpen={isOpenErrorModal}
        handleClose={handleOnCloseErrorModal}
      />
      <Modal bodyClassName="!w-auto" isOpen={isOpenModal} onClose={onCloseModal} heading={t('CreateTemplate')}>
        <div className="pt-4 mb-2">
          <Input
            inputClassName="!w-[30rem]"
            label={t('Name')}
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <ModalControlButtons
            onClose={onCloseModal}
            saveButtonText={t(isEditMode ? 'Update' : 'Create')}
            disabled={loading || !name.trim()}
            onSubmit={handleCreate}
          />
        </div>
      </Modal>
    </>
  );
};

export default CreateTemplateModal;
